import React, { useState } from 'react';
import {
  Container, Typography, Box, Paper, Grid, TextField, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Alert, CircularProgress, useMediaQuery, useTheme,
  Card, CardContent, Stack, Tooltip, InputAdornment,
} from '@mui/material';
import {
  Add, Edit, Delete, Category, Business, Label as LabelIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  useExpenseCategories, useCreateCategory, useUpdateCategory, useDeleteCategory,
  useSupplierCategories, useCreateSupplierCategory, useUpdateSupplierCategory, useDeleteSupplierCategory,
} from '../hooks/useExpenses';

const PRESET_COLORS = [
  '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#f97316', '#14b8a6', '#6366f1',
  '#84cc16', '#d946ef',
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#008060' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008060' },
  },
};

function CategoryTable({ categories, isLoading, onEdit, onDelete, type }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#008060' }} /></Box>;
  }

  if (!categories?.length) {
    return (
      <Box sx={{
        textAlign: 'center', py: 5,
        bgcolor: 'rgba(0,0,0,0.01)', borderRadius: 2,
        border: '2px dashed rgba(0,0,0,0.08)',
      }}>
        <Category sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Δεν υπάρχουν κατηγορίες. Προσθέστε μία πατώντας "Νέα Κατηγορία".
        </Typography>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {categories.map((cat) => (
          <Box key={cat.id} sx={{
            p: 2, borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            transition: 'all 0.2s',
            '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.01)' },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
              <Box sx={{
                width: 4, height: 40, borderRadius: 2,
                bgcolor: cat.color || '#6B7280',
              }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{cat.name}</Typography>
                {cat.description && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {cat.description}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1 }}>
              <Tooltip title="Επεξεργασία">
                <IconButton size="small" onClick={() => onEdit(cat)}
                  sx={{ '&:hover': { color: '#008060', bgcolor: 'rgba(0,128,96,0.08)' } }}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Διαγραφή">
                <IconButton size="small" onClick={() => onDelete(cat.id)}
                  sx={{ '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.06)' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
            <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, width: 50 }}>
              Χρώμα
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Όνομα
            </TableCell>
            {type === 'expense' && (
              <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Περιγραφή
              </TableCell>
            )}
            <TableCell align="right" sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, width: 100 }}>
              Ενέργειες
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categories.map((cat) => (
            <TableRow key={cat.id} sx={{ '&:hover': { bgcolor: 'rgba(0,128,96,0.02)' } }}>
              <TableCell>
                <Box sx={{
                  width: 24, height: 24, borderRadius: '8px',
                  bgcolor: cat.color || '#6B7280',
                  boxShadow: `0 2px 6px ${cat.color || '#6B7280'}40`,
                }} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>{cat.name}</Typography>
              </TableCell>
              {type === 'expense' && (
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{cat.description || '—'}</Typography>
                </TableCell>
              )}
              <TableCell align="right">
                <Tooltip title="Επεξεργασία">
                  <IconButton size="small" onClick={() => onEdit(cat)}
                    sx={{ '&:hover': { color: '#008060', bgcolor: 'rgba(0,128,96,0.08)' } }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Διαγραφή">
                  <IconButton size="small" onClick={() => onDelete(cat.id)}
                    sx={{ '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function CategoriesSettings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { data: expenseCategories = [], isLoading: expLoading } = useExpenseCategories();
  const createExpCat = useCreateCategory();
  const updateExpCat = useUpdateCategory();
  const deleteExpCat = useDeleteCategory();

  const { data: supplierCategories = [], isLoading: supLoading } = useSupplierCategories();
  const createSupCat = useCreateSupplierCategory();
  const updateSupCat = useUpdateSupplierCategory();
  const deleteSupCat = useDeleteSupplierCategory();

  const [dialog, setDialog] = useState({ open: false, type: null, editing: null });
  const [form, setForm] = useState({ name: '', description: '', color: '#6B7280' });
  const [error, setError] = useState('');

  const openDialog = (type, category = null) => {
    setForm(category
      ? { name: category.name, description: category.description || '', color: category.color || '#6B7280' }
      : { name: '', description: '', color: PRESET_COLORS[Math.floor(Math.random() * PRESET_COLORS.length)] }
    );
    setDialog({ open: true, type, editing: category });
    setError('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Το όνομα είναι υποχρεωτικό');
      return;
    }
    setError('');
    try {
      const data = { name: form.name.trim(), color: form.color };
      if (dialog.type === 'expense') data.description = form.description;
      if (dialog.editing) {
        if (dialog.type === 'expense') await updateExpCat.mutateAsync({ id: dialog.editing.id, data });
        else await updateSupCat.mutateAsync({ id: dialog.editing.id, data });
      } else {
        if (dialog.type === 'expense') await createExpCat.mutateAsync(data);
        else await createSupCat.mutateAsync(data);
      }
      setDialog({ open: false, type: null, editing: null });
    } catch (err) {
      setError(err.message || 'Σφάλμα κατά την αποθήκευση');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε αυτήν την κατηγορία;')) return;
    try {
      if (type === 'expense') await deleteExpCat.mutateAsync(id);
      else await deleteSupCat.mutateAsync(id);
    } catch (err) {
      alert(err.message || 'Σφάλμα κατά τη διαγραφή');
    }
  };

  const isSaving = createExpCat.isPending || updateExpCat.isPending || createSupCat.isPending || updateSupCat.isPending;

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, pb: 4, px: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            background: 'linear-gradient(135deg, #008060 0%, #004c3f 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(0, 128, 96, 0.3)',
          }}>
            <Category sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Κατηγορίες
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Διαχείριση κατηγοριών εξόδων και προμηθευτών
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Supplier Categories Section */}
      <Paper sx={{
        mb: 3, borderRadius: 3, overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Box sx={{
          p: { xs: 2, md: 2.5 },
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          bgcolor: 'rgba(0,0,0,0.01)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Business sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
                Κατηγορίες Προμηθευτών
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {supplierCategories.length} {supplierCategories.length === 1 ? 'κατηγορία' : 'κατηγορίες'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            size={isMobile ? 'small' : 'medium'}
            onClick={() => openDialog('supplier')}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' },
              boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
            }}
          >
            Νέα Κατηγορία
          </Button>
        </Box>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Κατηγοριοποιήστε τους προμηθευτές σας (π.χ. Τρόφιμα, Ποτά, Αναλώσιμα) για ανάλυση εξόδων.
          </Typography>
          <CategoryTable
            categories={supplierCategories}
            isLoading={supLoading}
            onEdit={(cat) => openDialog('supplier', cat)}
            onDelete={(id) => handleDelete('supplier', id)}
            type="supplier"
          />
        </Box>
      </Paper>

      {/* Expense Categories Section */}
      <Paper sx={{
        borderRadius: 3, overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Box sx={{
          p: { xs: 2, md: 2.5 },
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          bgcolor: 'rgba(0,0,0,0.01)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Category sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
                Κατηγορίες Εξόδων
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {expenseCategories.length} {expenseCategories.length === 1 ? 'κατηγορία' : 'κατηγορίες'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            size={isMobile ? 'small' : 'medium'}
            onClick={() => openDialog('expense')}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' },
              boxShadow: '0 4px 14px rgba(249,115,22,0.3)',
            }}
          >
            Νέα Κατηγορία
          </Button>
        </Box>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Κατηγορίες για γενικά έξοδα και τιμολόγια (π.χ. Ενοίκιο, ΔΕΗ, Τηλεφωνία).
          </Typography>
          <CategoryTable
            categories={expenseCategories}
            isLoading={expLoading}
            onEdit={(cat) => openDialog('expense', cat)}
            onDelete={(id) => handleDelete('expense', id)}
            type="expense"
          />
        </Box>
      </Paper>

      {/* Category Dialog */}
      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, type: null, editing: null })}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {dialog.editing ? 'Επεξεργασία Κατηγορίας' : 'Νέα Κατηγορία'}
          {dialog.type === 'supplier' ? ' Προμηθευτών' : ' Εξόδων'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1.5 }}>
            <TextField
              fullWidth
              required
              label="Όνομα"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LabelIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
              }}
              sx={inputSx}
            />
            {dialog.type === 'expense' && (
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Περιγραφή"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><DescriptionIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                }}
                sx={inputSx}
              />
            )}
            <Box>
              <Typography variant="caption" sx={{
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
                color: '#555', mb: 1.5, display: 'block',
              }}>
                Χρώμα
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {PRESET_COLORS.map((color) => (
                  <Tooltip key={color} title={color}>
                    <Box
                      onClick={() => setForm({ ...form, color })}
                      sx={{
                        width: 36, height: 36, borderRadius: '12px',
                        bgcolor: color, cursor: 'pointer',
                        border: form.color === color ? '3px solid #1a1a1a' : '3px solid transparent',
                        boxShadow: form.color === color ? `0 0 0 2px #fff, 0 0 0 4px ${color}` : 'none',
                        transition: 'all 0.15s',
                        '&:hover': { transform: 'scale(1.15)', boxShadow: `0 4px 12px ${color}50` },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
              {/* Preview */}
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 24, height: 24, borderRadius: '8px',
                  bgcolor: form.color,
                  boxShadow: `0 2px 6px ${form.color}40`,
                }} />
                <Typography variant="body2" color="text.secondary">
                  Προεπισκόπηση: <strong style={{ color: form.color }}>{form.name || 'Κατηγορία'}</strong>
                </Typography>
              </Box>
            </Box>
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog({ open: false, type: null, editing: null })} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Ακύρωση
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={isSaving}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3,
              boxShadow: '0 4px 14px rgba(0,128,96,0.3)',
            }}>
            {isSaving ? <CircularProgress size={20} /> : 'Αποθήκευση'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
